// Loader for /api/portal-config — the SPA's pre-auth bootstrap. Hits an
// unauthenticated endpoint (the chicken-and-egg: we can't attach a
// bearer until we know which Okta tenant + client to log into, and that
// data lives at this URL).
//
// Used by the top-level <PortalConfigProvider>; everything below it
// reads the resolved config via the React context, not by re-fetching.

export interface PortalAuthConfig {
  okta_enabled: boolean;
  issuer: string | null;
  client_id: string | null;
  audience: string | null;
  admin_group: string;
}

export interface PortalDefaultsConfig {
  archive_bucket: string | null;
  output_bucket: string | null;
}

export interface PortalCostsConfig {
  cpu_hr_usd: number;
  mem_gib_hr_usd: number;
  per_1000_docs_search_usd: number;
}

export interface PortalFeaturesConfig {
  // True when the operator has an OpenAI API key configured. The
  // SPA hides the "Ask AI" button on the search step when false.
  ai_query_builder: boolean;
}

export interface PortalConfig {
  auth: PortalAuthConfig;
  defaults: PortalDefaultsConfig;
  costs: PortalCostsConfig;
  // Optional so older portals without the field still parse — the
  // SPA treats missing features as "all off".
  features?: PortalFeaturesConfig;
}

// Plain fetch — deliberately no Bearer header, since we can't have one
// yet. Backend route is unauthenticated for exactly this reason.
export async function loadPortalConfig(): Promise<PortalConfig> {
  const res = await fetch("/api/portal-config", {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`portal config fetch failed: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as PortalConfig;
}
